import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Button,
  Typography,
  Box,
  Grid,
  styled,
  CircularProgress,
} from "@mui/material";

import { useNavigate } from "react-router-dom";
import ToastSnackbar, { SnackbarHandle } from "../../common/ToastNotification";
import HomeIcon from "@mui/icons-material/Home";
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import { useApi } from "../../store/UserLoginContext";
import { useForm } from "react-hook-form";
import ControlledSelect from "../../common/ControlledComponents/ControlledSelect";
import { Get as GetClass } from "../../api/Control-Settings/manage-class";
import { GetSections } from "../../api/Exams-Management/manage-section";
import { useReactToPrint } from "react-to-print";
import { GetWardCategoryReport } from "../../api/Report-Center/ward-category-report";
import WardCategoryReportTemplate from "../../templates/WardCategoryReportTemplate";

const MyCustomButton = styled(Button)(({ theme }) => ({
  fontFamily: "Motiva Sans Bold",
  fontSize: "0.80rem",
  fontWeight: "700",
  border: "1px solid #edf3f5",
  borderRadius: "4px",
  background: "#2e186a",
  boxShadow: "0 16px 30px rgb(23 31 114 / 20%)",
  marginTop: "0rem",
  "&:hover": {
    color: "#fff",
    border: "1px solid rgb(255, 130, 92)",
    backgroundColor: "rgb(255, 130, 92)",
  },
}));



const WardCategoryReport = () => {
  const { currentSessionAY } = useApi();
  const snackbarRef = React.useRef<SnackbarHandle>(null);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sectionList, setSectionList] = useState<any>([]);
  const [classRecords, setClassRecords] = useState<any>([]);
  const [printData, setPrintData] = useState<any>([]);
  const [readyToPrint, setReadyToPrint] = useState(false);
  const containerRef = useRef();
  const [fetching, setFetching] = useState(false);


  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [classes_records, section_records] = await Promise.all([
          GetClass(),
          GetSections(),
        ]);

        if (classes_records && classes_records.result.documents?.length > 0) {
          setClassRecords(classes_records.result.documents);
        }
        if (section_records && section_records.result.documents?.length > 0) {
          setSectionList(section_records.result.documents);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        snackbarRef.current?.showSnackbar(`Error fetching data`, "error");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);


  const {
    handleSubmit,
    control,
    formState: { errors },
    trigger,
    setError,
    reset,
  } = useForm({
    defaultValues: {
      class_id: "",
      section_id: "",
    },
    mode: "onTouched",
  });

  const handlePrint = useReactToPrint({
    documentTitle: "",
    onAfterPrint: () => setReadyToPrint(false),
  }) as (content: () => HTMLElement | null) => () => void;

  const onResetHandler = () => {
    reset({
      class_id: "",
      section_id: "",
    });
  };


  const handleFetchStudents = async (data: any) => {
    console.log("Fetch Students Data: ", data);
    try{
      setFetching(true);

      const { class_id, section_id } = data;
      if (!class_id || !section_id) {
        snackbarRef.current?.showSnackbar(`Please select class and section`, "warning");
        return;
      }

      const payload = {
        class_id,
        section_id
      };
      const student_records = await GetWardCategoryReport(payload);
      if (student_records && student_records.result.length > 0) {
        console.log(student_records.result);
        student_records.result.sort((a: any, b: any) => {
          return a.roll_number - b.roll_number;
        });
        setPrintData(student_records.result);
        setReadyToPrint(true);

        setTimeout(() => {
          try{
            handlePrint(() => containerRef.current!)();
          } catch(error){
            console.log("Handling print function call error.");
            console.log(error);
          }
        })
      }

    } catch (error) {
      console.error("Error fetching students:", error);
      snackbarRef.current?.showSnackbar(`Error fetching students`, "error");
    } finally {
      setFetching(false);
    }
  }


  return (
    <>
      <ToastSnackbar ref={snackbarRef} />
      {
        readyToPrint && (
          <div
            style={{
              position: "absolute",
              left: "-9999px",
              top: "0px",
            }}
          >
            {/* @ts-ignore */}
            <WardCategoryReportTemplate ref={containerRef} data={printData} />
          </div>
        )
      }
      <Box
        display={"flex"}
        flexDirection={"row"}
        justifyContent={"space-between"}
        p={2}
        pb={0}
        gap={1}
      >
        <Box
          display={"flex"}
          flexDirection={"row"}
          justifyContent={"flex-start"}
          p={2}
          pb={0}
          gap={1}
        >
          <MyCustomButton
            variant="contained"
            startIcon={<ArrowLeftIcon />}
            onClick={() => navigate("..")}
          >
            Go Back
          </MyCustomButton>
          <MyCustomButton
            variant="contained"
            startIcon={<HomeIcon />}
            onClick={() => navigate("/home")}
          >
            Home
          </MyCustomButton>
        </Box>
      </Box>

      <Box
        display={"flex"}
        flexDirection={"column"}
        p={2}
        pt={0}
        height="auto"
        width="auto"
      >
        <Box
          display={"flex"}
          flexDirection={"column"}
          p={2}
          pt={0}
          height="auto"
          justifyContent={"center"}
          alignItems={"center"}
          width={"90vw"}
        >
          <Typography variant="h6" alignSelf={"center"}>
            <strong>Select Class And Section To Fetch Ward Category Report</strong>
          </Typography>
          <Box
            display={"flex"}
            flexDirection={"row"}
            justifyContent={"space-evenly"}
            gap={3}
            width="auto"
            mt={4}
          >
            {loading ? (
              <Box>
                <CircularProgress />
              </Box>
            ) : (
              <Box
                display={"flex"}
                flexDirection={"column"}
                justifyContent={"center"}
                alignItems={"center"}
              >
                <form
                  onSubmit={handleSubmit(handleFetchStudents)}
                  onReset={onResetHandler}
                >
                  <Box
                    display={"flex"}
                    flexDirection={"row"}
                    justifyContent={"space-evenly"}
                    gap={3}
                    width="78vw"
                  >
                    <ControlledSelect
                        name={`class_id`}
                        control={control}
                        errors={errors}
                        label="Class"
                        options={classRecords.map((item: any) => ({
                          value: item.class_id,
                          label: item.name,
                        }))}
                        sx={{ width: "30%" }}
                        // selectProps={{ onChange: HandleClassChange }}
                      />
                      <ControlledSelect
                        name={`section_id`}
                        control={control}
                        errors={errors}
                        label="Section"
                        options={sectionList.map((item: any) => ({
                          value: item.section_id,
                          label: item.name,
                        }))}
                        sx={{ width: "30%" }}
                        // selectProps={{ onChange: HandleClassChange }}
                      />
                      <MyCustomButton
                        variant="contained"
                        type="submit"
                        startIcon={fetching ? <CircularProgress size={20} /> : null}
                        disabled={fetching}
                        sx={{
                          alignSelf: "center",
                          height: "70%",
                          width: "20%",
                        }}
                      >
                        {fetching ? "" : "Fetch Report"}
                      </MyCustomButton>
                      <MyCustomButton
                        variant="contained"
                        type="reset"
                        sx={{
                          alignSelf: "center",
                          height: "70%",
                          width: "20%",
                        }}
                      >
                        Clear
                      </MyCustomButton>
                  </Box>
                </form>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default WardCategoryReport;